import { useEffect, useState, useCallback } from 'react';
import {
  listMinistryInstruments,
  createMinistryInstrument,
  deleteMinistryInstrument,
} from '../../../api/instruments';
import type { Instrument, InstrumentCategory } from '../../../types/instrument';
import { CATEGORY_LABELS } from '../../../types/instrument';

const CATEGORIES: InstrumentCategory[] = ['STRINGS', 'KEYS', 'PERCUSSION', 'VOCALS', 'OPERATIONS', 'OTHER'];

const categoryColors: Record<InstrumentCategory, string> = {
  STRINGS: '#C8A96E',
  KEYS: '#A97EC8',
  PERCUSSION: '#E8A44C',
  VOCALS: '#7EB8A4',
  OPERATIONS: '#7E9EC8',
  OTHER: '#8E8E8E',
};

interface Props {
  ministryId: string;
  isAdmin: boolean;
}

export default function InstrumentCatalog({ ministryId, isAdmin }: Props) {
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [addModal, setAddModal] = useState(false);
  const [form, setForm] = useState({ name: '', category: 'STRINGS' as InstrumentCategory });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadInstruments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listMinistryInstruments(ministryId);
      setInstruments(data);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, [ministryId]);

  useEffect(() => {
    loadInstruments();
  }, [loadInstruments]);

  const toggleCategory = (catId: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  };

  const handleAdd = () => {
    setForm({ name: '', category: 'STRINGS' });
    setAddModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await createMinistryInstrument(ministryId, form.name.trim(), form.category);
      setAddModal(false);
      await loadInstruments();
    } catch { /* ignore */ } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMinistryInstrument(ministryId, id);
      await loadInstruments();
    } catch { /* ignore */ }
  };

  const groupedInstruments = CATEGORIES.map((cat) => ({
    id: cat,
    name: CATEGORY_LABELS[cat],
    instruments: instruments.filter((i) => i.category === cat),
  }));

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-white/5 bg-[#0D0C09]">
        <div>
          <h1
            className="text-white font-semibold text-lg"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Instrumentos e Vozes
          </h1>
          <p className="text-white/30 text-xs mt-0.5">{instruments.length} instrumentos cadastrados</p>
        </div>
        {isAdmin && (
          <button
            onClick={handleAdd}
            className="flex items-center gap-2 bg-[#C8A96E] text-[#0A0A0F] font-semibold px-4 py-2 rounded-lg text-sm cursor-pointer hover:bg-[#D4B87A] transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line text-sm"></i>
            Adicionar
          </button>
        )}
      </div>

      {/* Instruments by Category */}
      <div className="flex-1 overflow-y-auto px-6 md:px-8 py-6">
        {loading ? (
          <p className="text-white/30 text-sm">Carregando...</p>
        ) : (
          <div className="max-w-2xl flex flex-col gap-2">
            {groupedInstruments.map((group) => {
              if (group.instruments.length === 0) return null;
              return (
                <div key={group.id} className="bg-white/[0.02] border border-white/5 rounded-xl overflow-hidden">
                  {/* Category Header */}
                  <button
                    onClick={() => toggleCategory(group.id)}
                    className="w-full flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-white/[0.03] transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: categoryColors[group.id] }}
                      ></span>
                      <span className="text-white text-sm font-medium">{group.name}</span>
                      <span className="text-white/20 text-xs">{group.instruments.length}</span>
                    </div>
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i
                        className={`${
                          collapsed.has(group.id) ? 'ri-arrow-left-s-line' : 'ri-arrow-down-s-line'
                        } text-white/30 text-sm`}
                      ></i>
                    </div>
                  </button>

                  {/* Instrument Items */}
                  {!collapsed.has(group.id) && (
                    <div className="border-t border-white/5">
                      {group.instruments.map((inst) => (
                        <div
                          key={inst.id}
                          className="flex items-center gap-3 px-5 py-3 border-b border-white/5 last:border-b-0"
                        >
                          <span className="text-white/80 text-sm flex-1">{inst.name}</span>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full ${
                              inst.is_global
                                ? 'bg-white/5 text-white/30'
                                : 'bg-[#7EB8A4]/10 text-[#7EB8A4]'
                            }`}
                          >
                            {inst.is_global ? 'Sistema' : 'Customizado'}
                          </span>

                          {!inst.is_global && isAdmin && (
                            <button
                              onClick={() => handleDelete(inst.id)}
                              className="w-6 h-6 flex items-center justify-center text-white/30 hover:text-red-400 cursor-pointer"
                            >
                              <i className="ri-delete-bin-line text-xs"></i>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Modal */}
      {addModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setAddModal(false)}
          ></div>
          <div className="relative bg-[#1A1915] border border-white/10 rounded-2xl p-6 w-full max-w-md mx-4 z-10">
            <h3
              className="text-white font-semibold mb-5"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Adicionar Instrumento
            </h3>

            <div className="flex flex-col gap-4">
              <div>
                <label className="text-white/40 text-xs uppercase tracking-widest mb-1.5 block">
                  Nome
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Nome do instrumento"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 outline-none focus:border-[#C8A96E]/50 transition-colors"
                />
              </div>

              <div>
                <label className="text-white/40 text-xs uppercase tracking-widest mb-1.5 block">
                  Categoria
                </label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value as InstrumentCategory })}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-[#C8A96E]/50 transition-colors cursor-pointer"
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat} className="bg-[#1A1915]">
                      {CATEGORY_LABELS[cat]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setAddModal(false)}
                className="flex-1 px-5 py-3 border border-white/10 text-white/60 hover:text-white rounded-xl text-sm cursor-pointer transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="flex-1 px-5 py-3 bg-[#C8A96E] text-[#0A0A0F] font-semibold rounded-xl text-sm cursor-pointer hover:bg-[#D4B87A] disabled:opacity-50 transition-colors whitespace-nowrap"
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
